import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createForecastView, fetchForecastViews, updateForecastView } from '@/api/client'
import { ApiError } from '@/api/client'
import type { ForecastView, StageCategory } from '@/api/types'

import { Eye } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const ALL_CATEGORIES: StageCategory[] = ['committed', 'open_pipeline', 'lost']

export function ViewsAdminPage() {
  const qc = useQueryClient()
  const viewsQuery = useQuery({
    queryKey: ['config', 'views', 'admin'],
    queryFn: fetchForecastViews,
  })
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    code: '',
    name: '',
    apply_weighting: true,
    categories: ['committed', 'open_pipeline'] as string[],
    is_default: false,
  })

  const createMut = useMutation({
    mutationFn: createForecastView,
    onSuccess: async () => {
      setError(null)
      setDraft({
        code: '',
        name: '',
        apply_weighting: true,
        categories: ['committed', 'open_pipeline'],
        is_default: false,
      })
      await qc.invalidateQueries({ queryKey: ['config', 'views'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create view')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateForecastView>[1] }) =>
      updateForecastView(id, body),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'views'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update view')
    },
  })

  const views = viewsQuery.data ?? []

  return (
    <AdminSection
      title="Forecast views"
      tone="indigo"
      icon={Eye}
      lede="Define weighting and which stage categories each forecast view includes."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Weighted</th>
              <th>Categories</th>
              <th>Default</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {views.map((view) => (
              <ViewRow
                key={view.id}
                view={view}
                busy={patchMut.isPending}
                onSave={(body) => patchMut.mutate({ id: view.id, body })}
              />
            ))}
          </tbody>
        </table>
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          createMut.mutate({
            code: draft.code.trim(),
            name: draft.name.trim(),
            apply_weighting: draft.apply_weighting,
            categories: draft.categories,
            is_default: draft.is_default || undefined,
          })
        }}
      >
        <h3 className="admin-form__title">Add view</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="field__input"
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              required
            />
          </label>
          <label className="field field--checkbox">
            <input
              type="checkbox"
              checked={draft.apply_weighting}
              onChange={(e) => setDraft((d) => ({ ...d, apply_weighting: e.target.checked }))}
            />
            <span className="field__label">Apply weighting</span>
          </label>
          <label className="field field--checkbox">
            <input
              type="checkbox"
              checked={draft.is_default}
              onChange={(e) => setDraft((d) => ({ ...d, is_default: e.target.checked }))}
            />
            <span className="field__label">Default</span>
          </label>
        </div>
        <fieldset className="admin-form__fieldset">
          <legend className="field__label">Categories</legend>
          <div className="admin-form__checks">
            {ALL_CATEGORIES.map((cat) => (
              <label key={cat} className="field field--checkbox">
                <input
                  type="checkbox"
                  checked={draft.categories.includes(cat)}
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      categories: e.target.checked
                        ? [...d.categories, cat]
                        : d.categories.filter((c) => c !== cat),
                    }))
                  }}
                />
                <span>{cat}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <button className="button button--primary" type="submit" disabled={createMut.isPending}>
          Add view
        </button>
      </form>
    </AdminSection>
  )
}

function ViewRow({
  view,
  busy,
  onSave,
}: {
  view: ForecastView
  busy: boolean
  onSave: (body: Parameters<typeof updateForecastView>[1]) => void
}) {
  const weighted = view.apply_weighting ?? view.weighted ?? false
  const [name, setName] = useState(view.name)
  const [applyWeighting, setApplyWeighting] = useState(weighted)
  const [categories, setCategories] = useState<string[]>(view.categories ?? [])
  const [isDefault, setIsDefault] = useState(Boolean(view.is_default))

  const toggleCategory = (cat: StageCategory, on: boolean) => {
    setCategories((prev) => (on ? [...prev, cat] : prev.filter((c) => c !== cat)))
  }

  return (
    <tr>
      <td>
        <input
          className="field__input field__input--compact"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td>
        <code>{view.code}</code>
      </td>
      <td>
        <input
          type="checkbox"
          checked={applyWeighting}
          onChange={(e) => setApplyWeighting(e.target.checked)}
          aria-label="Apply weighting"
        />
      </td>
      <td>
        <div className="admin-form__checks admin-form__checks--inline">
          {ALL_CATEGORIES.map((cat) => (
            <label key={cat} className="field field--checkbox">
              <input
                type="checkbox"
                checked={categories.includes(cat)}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
              />
              <span>{cat}</span>
            </label>
          ))}
        </div>
      </td>
      <td>
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          aria-label="Default view"
        />
      </td>
      <td className="table__actions">
        <button
          type="button"
          className="button button--ghost"
          disabled={busy}
          onClick={() =>
            onSave({
              name,
              apply_weighting: applyWeighting,
              categories,
              is_default: isDefault,
            })
          }
        >
          Save
        </button>
      </td>
    </tr>
  )
}
